import type { EventInput } from "@fullcalendar/react";
import type { CalendarEventResponse } from "calendar/_schemas/calendar-event-schema";
import {
  calendarEventFieldLimits,
  calendarWeekdayAbbreviationLength,
} from "calendar/calendar-event-fields";
import { localDateInputLength, toLocalDateInput } from "_shared/dates/date-input";

export const calendarEventColors = {
  custom: "#1976d2",
  proposal: "#ed6c02",
  workAgreement: "#2e7d32",
  unknown: "#616161",
} as const;

const calendarEventTextColor = "#ffffff";

export type CalendarEventKind = keyof typeof calendarEventColors;

export const calendarEventKindLabels: Record<CalendarEventKind, string> = {
  custom: "Custom event",
  proposal: "Proposal",
  workAgreement: "Work Agreement",
  unknown: "Other managed event",
};

export function getCalendarEventKind(event: Pick<CalendarEventResponse, "source">): CalendarEventKind {
  const resourceType = event.source?.resourceType.toLowerCase();
  if (!resourceType) return "custom";
  if (resourceType === "proposal") return "proposal";
  if (resourceType === "workagreement" || resourceType === "work agreement") return "workAgreement";
  return "unknown";
}

export function getCalendarEventOccurrenceKey(event: Pick<CalendarEventResponse, "id" | "start">): string {
  return `${event.id}:${String(event.start)}`;
}

export function getCalendarEventDateKey(event: Pick<CalendarEventResponse, "isAllDay" | "start">): string {
  if (event.isAllDay) return String(event.start).slice(0, localDateInputLength);
  return toLocalDateInput(new Date(String(event.start)));
}

export function groupCalendarEventKindsByDate(events: CalendarEventResponse[]): Map<string, CalendarEventKind[]> {
  const grouped = new Map<string, CalendarEventKind[]>();
  for (const event of events) {
    const date = getCalendarEventDateKey(event);
    grouped.set(date, [...(grouped.get(date) ?? []), getCalendarEventKind(event)]);
  }
  return grouped;
}

export function groupCalendarEventsByDate(events: CalendarEventResponse[]): Map<string, CalendarEventResponse[]> {
  const grouped = new Map<string, CalendarEventResponse[]>();
  for (const event of events) {
    const date = getCalendarEventDateKey(event);
    grouped.set(date, [...(grouped.get(date) ?? []), event]);
  }
  return grouped;
}

export function toFullCalendarEvent(event: CalendarEventResponse): EventInput {
  const kind = getCalendarEventKind(event);
  return {
    id: getCalendarEventOccurrenceKey(event),
    title: event.title,
    start: String(event.start),
    ...(event.end !== null ? { end: String(event.end) } : {}),
    allDay: event.isAllDay,
    // FullCalendar 7 reads color/contrastColor; its v6 backgroundColor/borderColor/textColor land in
    // extendedProps and every event drew in the theme's default blue.
    color: calendarEventColors[kind],
    contrastColor: calendarEventTextColor,
    editable: false,
    extendedProps: {
      calendarEventId: event.id,
      calendarEventKind: kind,
      calendarEventPreview: getCalendarEventPreview(event),
      canEdit: event.canEdit,
      isSourceManaged: event.isSourceManaged,
      link: event.link,
      recurrence: event.recurrence,
    },
  };
}

/** What the hover card shows for one occurrence, already formatted for the viewer's locale. */
export type CalendarEventPreview = {
  kind: CalendarEventKind;
  title: string;
  /** The day or span of days, e.g. "Mon, Sep 14, 2026" or "Sep 14 – 16, 2026". */
  date: string;
  /** "All day", "9:00 – 10:00 AM", or null when `date` already carries the times of a multi-day span. */
  time: string | null;
  repeats: string | null;
  /** The zone the event was scheduled in, only when it differs from the viewer's own. */
  timeZoneId: string | null;
  description: string | null;
  /** Why the viewer cannot change the event, or null when they can. */
  access: { reason: "readOnly" | "sourceManaged"; note: string } | null;
  actionHint: string;
};

const sourceManagedAccessNotes: Record<CalendarEventKind, string> = {
  custom: "Synced from its source",
  proposal: "Synced from the proposal",
  workAgreement: "Synced from the work agreement",
  unknown: "Synced from its source",
};

export function getCalendarEventPreview(
  event: CalendarEventResponse,
  viewerTimeZoneId: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): CalendarEventPreview {
  const kind = getCalendarEventKind(event);
  const isEditable = event.canEdit && !event.isSourceManaged;
  const description = event.description?.trim();
  return {
    kind,
    title: event.title,
    ...formatPreviewSchedule(event),
    repeats: event.recurrence ? formatPreviewRecurrence(event.recurrence) : null,
    timeZoneId: !event.isAllDay && event.timeZoneId && event.timeZoneId !== viewerTimeZoneId
      ? event.timeZoneId
      : null,
    description: description ? truncatePreviewText(description) : null,
    access: event.isSourceManaged
      ? { reason: "sourceManaged", note: sourceManagedAccessNotes[kind] }
      : event.canEdit ? null : { reason: "readOnly", note: "Read-only" },
    actionHint: isEditable ? "Click to view or edit" : "Click for details",
  };
}

export function isCalendarEventPreview(value: unknown): value is CalendarEventPreview {
  return typeof value === "object" && value !== null
    && "kind" in value && typeof value.kind === "string" && value.kind in calendarEventKindLabels
    && "title" in value && typeof value.title === "string"
    && "date" in value && typeof value.date === "string";
}

export const calendarEventLegend = [
  { kind: "custom" as const, label: calendarEventKindLabels.custom },
  { kind: "proposal" as const, label: calendarEventKindLabels.proposal },
  { kind: "workAgreement" as const, label: calendarEventKindLabels.workAgreement },
  { kind: "unknown" as const, label: calendarEventKindLabels.unknown },
];

function formatPreviewSchedule(
  event: Pick<CalendarEventResponse, "end" | "isAllDay" | "start">,
): Pick<CalendarEventPreview, "date" | "time"> {
  if (event.isAllDay) {
    const start = parsePreviewDate(String(event.start));
    const end = event.end === null ? start : parsePreviewDate(String(event.end));
    // An all-day end is exclusive: an event on the 14th alone ends on the 15th.
    if (event.end !== null) end.setDate(end.getDate() - 1);
    const dayCount = Math.round((end.getTime() - start.getTime()) / millisecondsPerDay) + 1;
    return dayCount > 1
      ? { date: previewDateFormat().formatRange(start, end), time: `All day · ${dayCount} days` }
      : { date: previewDayFormat().format(start), time: "All day" };
  }

  const start = new Date(String(event.start));
  if (event.end === null) return { date: previewDayFormat().format(start), time: previewTimeFormat().format(start) };
  const end = new Date(String(event.end));
  return toLocalDateInput(start) === toLocalDateInput(end)
    ? { date: previewDayFormat().format(start), time: previewTimeFormat().formatRange(start, end) }
    : { date: previewDateTimeFormat().formatRange(start, end), time: null };
}

function formatPreviewRecurrence(recurrence: NonNullable<CalendarEventResponse["recurrence"]>): string {
  const units = { daily: "day", weekly: "week", monthly: "month", yearly: "year" } as const;
  const unit = units[recurrence.frequency];
  const interval = recurrence.interval === 1 ? `Every ${unit}` : `Every ${recurrence.interval} ${unit}s`;
  const weekdays = recurrence.weekdays.length > 0
    ? ` on ${recurrence.weekdays.map((weekday) => weekday
      .slice(0, calendarWeekdayAbbreviationLength)
      .replace(/^./, (value) => value.toUpperCase())).join(", ")}`
    : "";
  if (recurrence.count) return `${interval}${weekdays}, ${recurrence.count} occurrences`;
  if (recurrence.untilDate) return `${interval}${weekdays}, through ${previewDateFormat().format(parsePreviewDate(recurrence.untilDate))}`;
  if (recurrence.untilUtc) return `${interval}${weekdays}, through ${previewDateTimeFormat().format(new Date(recurrence.untilUtc))}`;
  return `${interval}${weekdays}`;
}

const millisecondsPerDay = 86_400_000;

function parsePreviewDate(value: string): Date {
  return new Date(`${value.slice(0, localDateInputLength)}T00:00:00`);
}

function previewDayFormat(): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function previewDateFormat(): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });
}

function previewTimeFormat(): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(undefined, { timeStyle: "short" });
}

function previewDateTimeFormat(): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function truncatePreviewText(value: string): string {
  const ellipsis = "…";
  const visibleLength = calendarEventFieldLimits.previewDescription - ellipsis.length;
  return value.length <= calendarEventFieldLimits.previewDescription
    ? value
    : `${value.slice(0, visibleLength)}${ellipsis}`;
}
